import { useState, useRef, useEffect } from "react";
import gsap from "gsap";
import { sendVistaMessage } from "../../services/vistaAI";
import { useAuth } from "../../context/AuthContext";
import { hasCreatorAccess } from "../../utils/authRoles";
import { CONTENT_CATALOG } from "../../data/contentCatalog";
import orbActive from "../../assets/images/vista-orb-active.png";
import niaAvatar from "../../assets/figma/profile/nia-avatar.png";
import "./vistaai.scss";

const ASK_VISTA_PILL_ART = `${process.env.PUBLIC_URL || ""}/figma/ask-vista-orb.svg`;

const COLLECTIBLE_CARDS = CONTENT_CATALOG
  .filter((item) => ["1", "5", "7"].includes(item.id))
  .map((item, index) => ({
    id: item.id,
    title: ["Silver Flakes", "Dan Curry", "Lara Lakes"][index] || item.title,
    artist: item.artist,
    type: "Performance",
    image: item.image || item.artwork,
  }));

const MOCK_CHAT_MESSAGES = [
  {
    role: "user",
    content: "What is a collectible here exactly? I need to know more",
  },
  {
    role: "assistant",
    content:
      "A collectible is a limited piece of a creator or artist that you can keep in your Vault. It's tied to this performance and released in a set number of editions.",
  },
  {
    role: "user",
    content: "so it's not just saving the show?",
  },
  {
    role: "assistant",
    content:
      "It's a collectible released by the artist as part of this performance, a piece of the moment you can keep. Once collected, it's saved in your Vault so you can revisit it later as part of your personal archive from this set.",
  },
  {
    role: "user",
    content: "Show me music collectables available right now",
  },
  {
    role: "assistant",
    content:
      "Here are some current music collectables you could collect. I've included Silver Lakes with a subtle new wave song.",
    cards: COLLECTIBLE_CARDS,
  },
];

function shouldUseMockChatState() {
  if (typeof window === "undefined") return false;

  const params = new URLSearchParams(window.location.search);
  const state = params.get("vistaState") || params.get("vista");
  return ["ai2", "mock", "chat"].includes(String(state || "").toLowerCase());
}

export default function VistaAI({ onClose }) {
  const { user } = useAuth();
  const firstName = user?.displayName?.split(' ')[0] || 'there';
  const userAvatar = user?.photoURL || user?.avatarUrl || user?.avatarURL || niaAvatar;
  const isCreator = hasCreatorAccess(user);
  const [messages, setMessages] = useState(() =>
    shouldUseMockChatState() ? MOCK_CHAT_MESSAGES : []
  );
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatRef = useRef(null);
  const panelRef = useRef(null);
  const shouldPinChatTopRef = useRef(shouldUseMockChatState());
  const hasChat = messages.length > 0 || loading;

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = shouldPinChatTopRef.current ? 0 : chatRef.current.scrollHeight;
      shouldPinChatTopRef.current = false;
    }
  }, [messages]);

  // GSAP slide-in from right
  useEffect(() => {
    if (panelRef.current) {
      gsap.fromTo(
        panelRef.current,
        { x: "100%", opacity: 0 },
        { x: "0%", opacity: 1, duration: 0.4, ease: "power2.out" }
      );
    }
  }, []);

  const handleClose = () => {
    if (panelRef.current) {
      gsap.to(panelRef.current, {
        x: "100%",
        opacity: 0,
        duration: 0.3,
        ease: "power2.in",
        onComplete: onClose,
      });
    } else if (onClose) {
      onClose();
    }
  };

  const handleSend = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: "user", content: text.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");

    if (process.env.REACT_APP_VISTA_USE_LIVE !== "true") {
      shouldPinChatTopRef.current = true;
      setMessages(MOCK_CHAT_MESSAGES);
      return;
    }

    setLoading(true);
    const reply = await sendVistaMessage(newMessages);
    setMessages([...newMessages, { role: "assistant", content: reply }]);
    setLoading(false);
  };

  return (
    <div className={`vista-ai${hasChat ? " vista-ai--chat" : ""}`} ref={panelRef}>
      <div className="vista-ai__header">
        {hasChat && (
          <>
            <span className="vista-ai__header-orb" aria-hidden="true">
              <img src={ASK_VISTA_PILL_ART} alt="" />
            </span>
            <span className="vista-ai__title">Ask Vista</span>
          </>
        )}
        <button className="vista-ai__close" onClick={handleClose} aria-label="Close Vista AI">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* Main content area */}
      <div className="vista-ai__main">
        {!hasChat && (
          <div className="vista-ai__top">
            <span className={`vista-ai__orb-shell${loading ? " vista-ai__orb-shell--working" : ""}`} aria-hidden="true">
              <img className="vista-ai__orb" src={orbActive} alt="" />
            </span>
            <div className="vista-ai__greeting">
              <span className="vista-ai__greeting-hi">Hi {firstName},</span>
              <span className="vista-ai__greeting-text">
                Welcome! How can I Help?
              </span>
            </div>
            <p className="vista-ai__desc">
              Calm prompts. Quiet guidance. Suggestions that stay out of the way
              while you listen, save, collect, and return.
            </p>
          </div>
        )}

        {hasChat ? (
          <div className="vista-ai__chat" ref={chatRef}>
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`vista-ai__msg-wrap vista-ai__msg-wrap--${msg.role}${msg.cards?.length > 0 ? " vista-ai__msg-wrap--cards" : ""}`}
              >
                <div className={`vista-ai__msg vista-ai__msg--${msg.role}${msg.cards?.length > 0 ? " vista-ai__msg--cards" : ""}`}>
                  <span className={`vista-ai__avatar vista-ai__avatar--${msg.role}`} aria-hidden="true">
                    <img src={msg.role === "assistant" ? ASK_VISTA_PILL_ART : userAvatar} alt="" />
                  </span>
                  <div className="vista-ai__msg-body">
                    <span>{msg.content}</span>
                    {msg.cards?.length > 0 && (
                      <div className="vista-ai__cards" aria-label="Collectible suggestions">
                        {msg.cards.map((card) => (
                          <article className="vista-ai__card" key={card.id}>
                            <img src={card.image} alt="" />
                            <span className="vista-ai__card-badge">Verified</span>
                            <span className="vista-ai__card-copy">
                              <strong>{card.title}</strong>
                              <span>{card.artist}</span>
                            </span>
                            <span className="vista-ai__card-type">{card.type}</span>
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="vista-ai__msg-wrap vista-ai__msg-wrap--assistant">
                <div className="vista-ai__msg vista-ai__msg--assistant vista-ai__msg--loading">
                  <span className="vista-ai__avatar vista-ai__avatar--assistant" aria-hidden="true">
                    <img src={ASK_VISTA_PILL_ART} alt="" />
                  </span>
                  <div className="vista-ai__msg-body">Listening...</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="vista-ai__welcome-space" aria-hidden="true" />
        )}

        {/* Input bar */}
        <div className="vista-ai__input-bar">
          <div className="vista-ai__input-left">
            <div className="vista-ai__search-icon">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="7" cy="7" r="5" stroke="#C4C669" strokeWidth="1.5" />
                <line
                  x1="11"
                  y1="11"
                  x2="14"
                  y2="14"
                  stroke="#C4C669"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <form
              className="vista-ai__input-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleSend(input);
              }}
            >
              <input
                type="text"
                placeholder="Ask Vista"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={loading}
              />
            </form>
          </div>
          <button
            className="vista-ai__send"
            type="button"
            aria-label="Send Vista message"
            disabled={loading || !input.trim()}
            onClick={() => handleSend(input)}
          >
            <svg
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M19.6511 9.6525C19.6511 9.74722 19.6242 9.83999 19.5736 9.92001C19.5229 10 19.4505 10.064 19.3649 10.1045L1.3649 18.6045C1.275 18.6481 1.17406 18.6636 1.07521 18.6491C0.976365 18.6345 0.884174 18.5906 0.810623 18.523C0.737071 18.4554 0.685559 18.3672 0.662782 18.2699C0.640005 18.1726 0.647016 18.0707 0.682904 17.9775L3.5249 10.3505C3.69258 9.90027 3.69258 9.40473 3.5249 8.9545L0.681903 1.3275C0.645834 1.23414 0.638731 1.13208 0.661519 1.03463C0.684308 0.937176 0.735932 0.84885 0.809659 0.781167C0.883387 0.713485 0.975797 0.669587 1.07484 0.655198C1.17388 0.64081 1.27496 0.656597 1.3649 0.700502L19.3649 9.2005C19.4505 9.241 19.5229 9.30498 19.5736 9.385C19.6242 9.46502 19.6511 9.55779 19.6511 9.6525ZM19.6511 9.6525L3.65088 9.65247"
                stroke="#013F2C"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {isCreator && (
            <span className="vista-ai__balance" aria-label="Creator balance">
              ~0.00 USD
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
